import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Media from 'react-media';
import { useForm } from 'react-hook-form/dist/index.ie11';
import isEmpty from 'lodash.isempty';

import verifyCaptchaQuery from 'mutations/verifyCaptcha';
import subscribeToNewsletterQuery from 'mutations/subscribeToNewsletter';
import GLOBAL_MEDIA_QUERIES from 'constants/Screen';
import trackAction from 'utils/tracking';
import { performMutation } from 'lib/api';

import IconSvg from 'components/Core/IconSvg';

import { NewsletterInput, NewsletterSubmitBtn, NotificationArrow, Notification } from './Newsletter.styles';

const Newsletter = ({ newsletterTitle, newsletterBody, hostname, recaptchaKey }) => {
  const { register, handleSubmit, errors } = useForm();
  const [stateValue, setStateValue] = useState({
    notificationType: null,
    notificationMessage: null,
  });
  const SITE_KEY = recaptchaKey;
  const onSubmit = async (data) => {
    const defaultErrorMessage = 'Er is iets mis gegaan, probeer het alstublieft nogmaals';
    try {
      const resp = await performMutation(subscribeToNewsletterQuery, { hostname, ...data });
      const { subscribeToNewsletter } = resp;

      if (subscribeToNewsletter && subscribeToNewsletter.errors && !isEmpty(subscribeToNewsletter.errors)) {
        const errorMessage =
          subscribeToNewsletter.errors[0].messages && subscribeToNewsletter.errors[0].messages[0]
            ? subscribeToNewsletter.errors[0].messages[0]
            : defaultErrorMessage;

        setStateValue({
          notificationType: 'error',
          notificationMessage: errorMessage,
        });
        trackAction('newsletter', 'fail', 'subscribe');
      } else {
        setStateValue({
          notificationType: 'success',
          notificationMessage: 'Je bent ingeschreven voor de nieuwsbrief!',
        });
        trackAction('newsletter', 'success', 'subscribe');
      }
    } catch (err) {
      setStateValue({
        notificationType: 'error',
        notificationMessage: defaultErrorMessage,
      });
      trackAction('newsletter', 'fail', 'subscribe');
    }
  };

  const verifyToken = async (token, data) => {
    try {
      const {
        verifyCaptcha: { success, action },
      } = await performMutation(verifyCaptchaQuery, { token, hostname });
      if (success && action === 'submit') {
        onSubmit(data);
      } else {
        setStateValue({
          notificationType: 'error',
          notificationMessage: 'Captcha validation failed!',
        });
      }
    } catch (err) {
      setStateValue({
        notificationType: 'error',
        notificationMessage: 'Captcha validation failed!',
      });
    }
  };

  const invokeChallenge = (data) => {
    window.grecaptcha.ready(() => {
      window.grecaptcha.execute(SITE_KEY, { action: 'submit' }).then((token) => {
        verifyToken(token, data);
      });
    });
  };

  useEffect(() => {
    // Add reCaptcha
    const script = document.createElement('script'); // eslint-disable-line no-undef
    script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    document.body.appendChild(script); // eslint-disable-line no-undef
  }, []);

  const { notificationType, notificationMessage } = stateValue;
  const hideFormError = notificationType && notificationMessage;

  // Hide the notification if there is error.
  if (errors && errors.email && notificationType && notificationMessage) {
    setStateValue({
      notificationType: null,
      notificationMessage: null,
    });
  }

  return (
    <div className="w-full bg-secondary rounded-t-base text-white">
      <div className="container mx-auto px-4 py-8 lg:px-20 lg:py-12 lg:flex">
        <div className="text-center lg:flex-1/2 lg:text-left">
          <div className="lg:w-5/6">
            <Media queries={GLOBAL_MEDIA_QUERIES} defaultMatches={{ lg: true }}>
              {(matches) => {
                if (matches.lg || matches.xl || matches.xxl) {
                  return <h2 className="mb-4">{newsletterTitle}</h2>;
                }
                return <h3 className="mb-4">{newsletterTitle}</h3>;
              }}
            </Media>
            <p className="mb-4">{newsletterBody}</p>
          </div>
        </div>
        <div className="lg:flex-1/2">
          <div className="lg:w-5/6">
            <div className="mb-4 lg:mt-12">
              <form className="flex" onSubmit={handleSubmit(invokeChallenge)}>
                <div className="g-recaptcha" data-sitekey={SITE_KEY} data-size="invisible" />
                <NewsletterInput
                  name="email"
                  placeholder="E-mailadres"
                  aria-label="E-mailadres"
                  aria-required="true"
                  autocomplete="off"
                  ref={register({
                    required: 'Email is verplicht',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Ongeldig email adres',
                    },
                  })}
                />
                <NewsletterSubmitBtn type="submit">
                  <Media queries={GLOBAL_MEDIA_QUERIES} defaultMatches={{ lg: true }}>
                    {(matches) => {
                      if (matches.xs || matches.sm) {
                        return <IconSvg icon="arrow-right" size={10} />;
                      }
                      return <IconSvg icon="arrow-right" size={13} />;
                    }}
                  </Media>
                </NewsletterSubmitBtn>
              </form>
            </div>
            {errors && errors.email && !hideFormError && (
              <Notification type="error">
                <NotificationArrow />
                {errors.email.message}
              </Notification>
            )}
            {notificationType && notificationMessage && (
              <Notification type={notificationType}>
                <NotificationArrow />
                {notificationMessage}
              </Notification>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

Newsletter.propTypes = {
  newsletterTitle: PropTypes.string.isRequired,
  newsletterBody: PropTypes.string.isRequired,
  hostname: PropTypes.string.isRequired,
  recaptchaKey: PropTypes.string.isRequired,
};

export default Newsletter;
